// =============================================
// Party › Document type › date layout  (acctg_party_doc_type, migration 123)
// =============================================
// One vendor prints dates differently on its different documents (pump receipt, counter
// invoice, statement). Masters keeps a layout per PARTY > DOCUMENT TYPE; this picks which
// of a party's document types a given document is, from the words printed on it.
//
// Pure + unit-tested (partyDocType.test.ts).
// =============================================

export interface PartyDocType {
  party_doc_type_id: number;
  party_kind: string;            // vendor | customer | other
  party_id: number;
  doc_type_name: string;
  match_words?: string | null;   // comma-separated; empty = look for the name
  date_format_id?: number | null;
  sort_order?: number | null;
}

const norm = (s: string) => ` ${String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()} `;

/** The phrases that identify a document type: its match words, else its name. */
export function matchPhrases(t: PartyDocType): string[] {
  const words = String(t.match_words || '').split(/[,;\n]+/).map(w => w.trim()).filter(Boolean);
  return words.length ? words : [t.doc_type_name];
}

/**
 * Which of ONE party's document types `text` is (document text + file name + any type
 * hint). The type whose longest phrase is found wins — "self checkout receipt" beats
 * "receipt". Nothing found: a party with a single document type gets that one; with
 * several, none (better no layout than the wrong one).
 */
export function matchPartyDocType(types: PartyDocType[], text: string): PartyDocType | null {
  const usable = types.filter(t => t.doc_type_name);
  if (!usable.length) return null;
  const best = phraseHit(usable, text);
  if (best) return best;
  return usable.length === 1 ? usable[0] : null;
}

/** The type whose longest phrase is printed in `text`, if any. */
function phraseHit(usable: PartyDocType[], text: string): PartyDocType | null {
  const hay = norm(text);
  let best: PartyDocType | null = null;
  let bestLen = 0;
  for (const t of usable) {
    for (const phrase of matchPhrases(t)) {
      const p = norm(phrase);
      if (p.trim().length < 2 || !hay.includes(p)) continue;
      if (p.length > bestLen) { best = t; bestLen = p.length; }
    }
  }
  return best;
}

const hasWords = (t: PartyDocType) => String(t.match_words || '').trim() !== '';

/**
 * Which of ONE party's document types to read an unclear date with.
 *
 * Words come first, as in matchPartyDocType. Without a deciding word, the printed date
 * itself narrows it down: `fits(type)` says whether the date could have been printed in
 * that type's layout (dateDisambiguate.formatFitsRawDate), and when every type it fits
 * shares one layout, that layout is the answer — a vendor whose gas receipt prints
 * YY/MM/DD and whose till receipt prints MM/DD/YYYY needs no words at all.
 *
 * A name-only hit ("Receipt") does not decide when the same name was split by layout
 * ("Receipt (MM/DD/YY)") — every receipt of that vendor carries the word. Still
 * undecided → null: the user is asked rather than given the wrong date.
 */
export function pickPartyDocType(types: PartyDocType[], text: string, fits: (t: PartyDocType) => boolean): PartyDocType | null {
  const usable = types.filter(t => t.doc_type_name && t.date_format_id);
  if (!usable.length) return null;
  const fitting = usable.filter(fits);
  const oneLayout = (list: PartyDocType[]) => list.length > 0 && list.every(t => t.date_format_id === list[0].date_format_id);

  const hit = phraseHit(usable, text);
  if (hit) {
    const split = !hasWords(hit) && usable.some(u => u !== hit && u.date_format_id !== hit.date_format_id &&
      norm(u.doc_type_name).startsWith(norm(hit.doc_type_name).trimEnd() + ' '));
    // the words say one type, the printed date another layout → the date is the evidence
    if (!split && (fits(hit) || !oneLayout(fitting))) return hit;
  }
  if (oneLayout(fitting)) return fitting[0];
  return !hit && usable.length === 1 ? usable[0] : null;
}

// ---------------------------------------------------------------------------
// Document types that add themselves
// ---------------------------------------------------------------------------

export type DocTypeLearnPlan =
  | { action: 'none' }
  | { action: 'fill'; id: number }       // an existing line had no date format yet
  | { action: 'add'; name: string };     // a layout this party is not known to print → its own line

/**
 * What a FILED document teaches Masters about its party's document types. `formatId` is
 * the layout the filed date proved.
 *   - the party already has a line with this layout            → nothing;
 *   - the line this document is (by words, or by its Nature of Document) has no layout → fill it;
 *   - otherwise the layout is new for this party               → add a line for it, named
 *     after the Nature of Document, or "<Nature> (<layout>)" when that name is taken.
 * A line the user removed (`removed`) is never put back — neither its name nor its
 * layout; a line the user set is never changed.
 */
export function planDocTypeLearning(o: {
  types: PartyDocType[];          // the party's live lines, with or without a layout
  removed?: PartyDocType[];
  docuTypeName: string;           // Nature of Document, e.g. "Expense Receipt"
  text: string;                   // document text + file name
  formatId: number | null;
  formatLabel: string;            // e.g. "MM/DD/YY"
}): DocTypeLearnPlan {
  if (!o.formatId) return { action: 'none' };
  const live = o.types.filter(t => t.doc_type_name);
  const gone = o.removed || [];
  if ([...live, ...gone].some(t => t.date_format_id === o.formatId)) return { action: 'none' };

  const key = (s: string) => norm(s).trim();
  const base = String(o.docuTypeName || '').trim() || 'Document';
  const hit = phraseHit(live.filter(hasWords), o.text) || live.find(t => key(t.doc_type_name) === key(base)) || null;
  if (hit && !hit.date_format_id) return { action: 'fill', id: hit.party_doc_type_id };

  const taken = new Set(live.map(t => key(t.doc_type_name)));
  const removed = new Set(gone.map(t => key(t.doc_type_name)));
  const name = (taken.has(key(base)) || removed.has(key(base)) ? `${base} (${o.formatLabel})` : base).slice(0, 100);
  if (taken.has(key(name)) || removed.has(key(name))) return { action: 'none' };
  return { action: 'add', name };
}
